import { array, ArraySchema, bool, mixed, number, object, string, StringSchema } from 'yup';
import moment from 'moment';

import i18n from 'config/i18n';
import {
  AssignmentOperator,
  AssignmentTargetUser,
  ClientProtectedFragment,
  ClientUrlParamsDynamicValues,
  ContentEmailType,
  GenericStatus,
  ManualNotificationType,
  ParamsStatus,
  S3ObjectInput,
} from 'api';
import { INVITE_REFERRAL_LIMIT } from 'app-constants';
import { getContentUrlRegex } from '../../helpers';

const MAX_MESSAGE_LENGTH = 140;
const MAX_SUBJECT_LENGTH = 70;
const DOMAIN_REGEX =
  // eslint-disable-next-line
  /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)+([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$/;

const userSegmentInvalid = i18n.t('admin:userSegmentInvalid');
const leaderboardSegmentsInvalid = i18n.t('admin:leaderboardsSegmentRequired');
const rangeDateError = i18n.t('admin:rangeDateError');
const hostInvalid = i18n.t('admin:hostInvalid');

const messageBodyLength = i18n.t('messages:messageBodyLength', { count: MAX_MESSAGE_LENGTH });
const messageBodyRequired = i18n.t('messages:messageBodyRequired');
const emailSubjectRequired = i18n.t('messages:subjectRequired');
const emailMessageRequired = i18n.t('messages:messageRequired');

const trackingKeyRequired = i18n.t('parameters:trackingKeyError');

const segmentRequired = i18n.t('contentEmails:segmentError');
const subjectRequired = i18n.t('contentEmails:subjectRequired');
const subjectLength = i18n.t('contentEmails:subjectLength', { count: MAX_SUBJECT_LENGTH });
const specRequired = i18n.t('contentEmails:specRequired');
const groupRequired = i18n.t('contentEmails:groupRequired');

const titleInvalid = i18n.t('admin:titleRequired');
const emailInvalid = i18n.t('admin:emailInvalid');
const emailRequired = i18n.t('admin:emailRequired');
const scheduleTimeInvalid = i18n.t('admin:scheduledTimeError');
const invalidUrl = i18n.t('admin:urlInvalid');
const contentUrlRequired = i18n.t('contentEmails:contentUrlRequired');
const contentUrlMismatch = i18n.t('contentEmails:contentUrlMismatch');

const disclosureInvalid = i18n.t('admin:disclosureInvalid');

const socialMediaPolicyError = i18n.t('admin:socialMediaPolicyError');

const referralHeadlineError = i18n.t('admin:referralHeadlineRequired');
const referralMessageError = i18n.t('admin:referralMessageRequired');
const referralMessageMax = i18n.t('admin:referralMessageMax', { count: INVITE_REFERRAL_LIMIT });

export const PeopleInviteSchema = object().shape(
  {
    uploadfile: object().when(
      'email_addresses',
      (emailAddresses: string[], schema: ArraySchema<string>) => {
        if (emailAddresses && emailAddresses.length) {
          return schema.nullable();
        }
        return schema.required();
      }
    ),

    email_addresses: array()
      .of(string().email())
      .when('uploadfile', (uploadfile: S3ObjectInput, schema: ArraySchema<string>) => {
        if (uploadfile) {
          return schema.nullable();
        }
        return schema.min(1).required();
      }),

    subject: string().nullable(true),
    message: string().nullable(true),
    email_from_address: string().email(emailInvalid).nullable(true),
  },
  [['uploadfile', 'email_addresses']]
);

export const DepartmentLocationSchema = object().shape({
  title: string().required(),
});

export const LeaderboardSchema = (allUsers?: boolean) => {
  return object().shape({
    id: string(),
    title: string().required().min(1).required(titleInvalid),
    description: string().nullable(true),
    segment_ids: array().test('is-valid', leaderboardSegmentsInvalid, function (value) {
      const { id } = this.parent;
      if (!id) {
        return true;
      }
      return allUsers || value.length >= 1;
    }),
    leaderboard_status: mixed()
      .oneOf([GenericStatus.Active, GenericStatus.Disabled])
      .nullable(true),
    start_at: number().nullable(true),
    end_at: number()
      .nullable(true)
      .test('is-after-start', rangeDateError, function (value) {
        const { start_at } = this.parent;
        if (!value || !start_at) {
          return true;
        }

        const startMoment = moment.unix(start_at);
        const endMoment = moment.unix(value);
        return endMoment.isAfter(startMoment);
      }),
    scoring_aggregate: object().shape({
      everyonesocial: object().shape({
        posting: array().of(
          object().shape({
            event: string().required(),
            score: number().required(),
          })
        ),
      }),
      linkedin: object().shape({
        general: array().of(
          object().shape({
            event: string().required(),
            score: number().required(),
          })
        ),
      }),
      twitter: object().shape({
        general: array().of(
          object().shape({
            event: string().required(),
            score: number().required(),
          })
        ),
      }),
      facebook: object().shape({
        general: array().of(
          object().shape({
            event: string().required(),
            score: number().required(),
          })
        ),
      }),
    }),
  });
};

export const LeaderboardPointsSchema = array().of(
  object().shape({
    event: string().required(),
    score: number().required(),
  })
);

export const SegmentSchema = object().shape({
  title: string().required(userSegmentInvalid),
});

export const EmailAddressesSchema = array().of(string().email().required());

export const ManualNotificationSchema = object().shape({
  message: object()
    .required()
    .shape({
      body: string().required(messageBodyRequired).max(MAX_MESSAGE_LENGTH, messageBodyLength),
      url: string().nullable(true).url(invalidUrl),
    }),
  email: object()
    .nullable(true)
    .shape({
      subject: string().required(emailSubjectRequired),
      body: string().hasHtml(emailMessageRequired),
    }),
  segment_ids: array().when('entire_company', {
    is: value => !!value,
    then: array(),
    otherwise: array().of(string()).required().min(1),
  }),
  notification_type: mixed().oneOf([ManualNotificationType.Scheduled, ManualNotificationType.Now]),
  send_at: mixed().when('notification_type', {
    is: value => value === ManualNotificationType.Scheduled,
    then: mixed().test('is-future', 'REQUIRED MESSAGE', (value: number) => {
      if (!value) {
        return true;
      }
      return moment.unix(value).isAfter();
    }),
    otherwise: mixed().nullable(true),
  }),
});

export const NewTrackingKeySchema = object().shape({
  key: string().required(trackingKeyRequired),
});

export const ClientUrlParamsConfigSchema = object().shape(
  {
    key: string().required(trackingKeyRequired),
    value: string().when(['dynamic_value', 'value_options'], {
      is: (dynamicValue: string, valueOptions: string[]) => {
        return !!(dynamicValue || valueOptions);
      },
      then: string().nullable(),
      otherwise: string().required(),
    }),
    dynamic_value: mixed().when(['value', 'value_options'], {
      is: (value: string, valueOptions: string[]) => {
        return !!(value || valueOptions);
      },
      then: string().nullable(),
      otherwise: string()
        .required()
        .oneOf([
          ClientUrlParamsDynamicValues.User,
          ClientUrlParamsDynamicValues.Network,
          ClientUrlParamsDynamicValues.ShareId,
        ]),
    }),
    default_option_value: string().when(
      'value_options',
      (valueOptions: string[], schema: StringSchema) => {
        return valueOptions ? schema.oneOf(valueOptions) : schema.nullable();
      }
    ),
    value_options: array()
      .of(string())
      .when(
        ['value', 'dynamic_value'],
        (value: string, dynamicValue: string, schema: ArraySchema<string>) => {
          if (value || dynamicValue) {
            return schema.nullable();
          }
          return schema.min(1).required();
        }
      ),
  },
  [
    ['value', 'dynamic_value'],
    ['value', 'value_options'],
    ['value_options', 'dynamic_value'],
  ]
);

export const ClientUrlParamsSchema = object().shape({
  params_status: mixed().oneOf([ParamsStatus.Active, ParamsStatus.Disabled]).required(),
  configuration: array().of(ClientUrlParamsConfigSchema),
});

export const AdminUpdateClient = object().shape({
  id: string().required(),
  content_domains: array()
    .of(string().required(invalidUrl).matches(DOMAIN_REGEX, hostInvalid))
    .required(),
});

export const ContentEmailSchema = (client: ClientProtectedFragment) => {
  const contentUrlRegex = getContentUrlRegex(client);

  return object().shape({
    title: string().required(titleInvalid),
    all_users: bool(),
    segment_ids: array().when('all_users', {
      is: value => !!value,
      then: array(),
      otherwise: array().of(string()).required(segmentRequired).min(1),
    }),
    send_at: number().test('is-future', scheduleTimeInvalid, function (value: number) {
      const { id } = this.parent;
      if (id) {
        return true;
      }
      return moment.unix(value).isAfter();
    }),
    email_from: string().email(emailRequired).required(emailRequired),
    email_reply_to: string().email(emailRequired).required(emailRequired),
    subject: string().required(subjectRequired).max(MAX_SUBJECT_LENGTH, subjectLength),
    content_specs: array()
      .of(
        object().shape({
          content_type: mixed().oneOf([
            ContentEmailType.Featured,
            ContentEmailType.Group,
            ContentEmailType.Timeline,
          ]),
          id: string().when(
            'content_type',
            (contentType: ContentEmailType, schema: StringSchema) => {
              switch (contentType) {
                case ContentEmailType.Featured:
                  return string()
                    .required(contentUrlRequired)
                    .matches(contentUrlRegex, contentUrlMismatch);
                case ContentEmailType.Group:
                  return string().required(groupRequired);
                case ContentEmailType.Timeline:
                  return schema.nullable();
              }
            }
          ),
        })
      )
      .compact()
      .min(1, specRequired),
  });
};

export const PrivacyPolicySchema = () =>
  object().shape({
    social_media_policy_title: string().required(titleInvalid),
    social_media_policy: string().hasHtml(socialMediaPolicyError),
    social_media_policy_enabled: bool(),
  });

export const DisclosuresSchema = () =>
  object().shape({
    disclosure: string().required(disclosureInvalid),
  });

export const ReferralSchema = object().shape({
  referral_headline: string().required(referralHeadlineError),
  referral_message: string()
    .required(referralMessageError)
    .max(INVITE_REFERRAL_LIMIT, referralMessageMax),
});

export const AccountSettingsSchema = object().shape({
  id: string().required(),
  title: string().required(titleInvalid),
  image: object()
    .shape({
      key: string().required(),
      region: string().required(),
      bucket: string().required(),
    })
    .nullable(),
  icon: object()
    .shape({
      key: string().required(),
      region: string().required(),
      bucket: string().required(),
    })
    .nullable(),
  content_domains: array()
    .of(string().required(invalidUrl).matches(DOMAIN_REGEX, hostInvalid))
    .required(),
  allow_user_signup: bool().required(),
  moderator_can_mark_as_important: bool().required(),
  allow_group_creation: bool().required(),
  allow_sharing: bool().required(),
});

export const AssignmentFormSchema = object().shape({
  title: string().required(titleInvalid),
  users_required: array().of(string()),
  users_suggested: array().of(string()),
  groups_required: array().of(string()),
  groups_suggested: array().of(string()),
  target_user: mixed()
    .oneOf([AssignmentTargetUser.New, AssignmentTargetUser.All, AssignmentTargetUser.Existing])
    .required(),
  rule: object().shape({
    all_users: bool(),
    operator: mixed().oneOf([AssignmentOperator.And, AssignmentOperator.Or]),
    department_id: array().of(string()),
    location_ids: array().of(string()),
  }),
});
